import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PropertyCard } from "@/components/PropertyCard";
import { LeafletMap } from "@/components/LeafletMap";
import { FilterBar, LOCALITIES } from "@/components/FilterBar";
import { SmartSearch } from "@/components/SmartSearch";
import { ArrowRight, MapPin, Shield, Users, Home as HomeIcon } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1707189856923-46dd41ea2bdc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export const Home = ({ onAuth }) => {
  const [featured, setFeatured] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    api.get("/properties", { params: { limit: 6 } }).then(({ data }) => setFeatured(data));
    api.get("/neighborhoods").then(({ data }) => setNeighborhoods(data));

    // Live-refreshing stats: refetch every 30s and whenever the tab regains focus
    const fetchStats = () => api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    fetchStats();
    const poll = setInterval(fetchStats, 30000);
    const onFocus = () => fetchStats();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      clearInterval(poll);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  const search = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    window.location.href = `/listings?${params.toString()}`;
  };

  return (
    <div className="pb-16">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 heritage-grid opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 lg:pt-16 grid lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-7 fade-in-up">
            <span className="section-eyebrow" data-testid="hero-eyebrow">Hyperlocal · Mysuru · Est. 2026</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--teak)] mt-4 leading-[1.05] tracking-tight">
              Find a home in <br />
              <span className="italic" style={{ color: "var(--primary)" }}>NimmaSwathu.</span><br />
              Neighborhood by neighborhood.
            </h1>
            <p className="mt-6 text-lg text-[var(--ink-muted)] max-w-xl leading-relaxed">
              From Kalidasa Road heritage villas to Gokulam yoga apartments and Vijayanagar family plots — discover
              every street, school and chai spot before you decide.
            </p>

            {/* Search */}
            {/* buy and sell */}
            <form onSubmit={search} className="mt-8 p-4 bg-white rounded-2xl shadow-sm border border-[var(--line)]" data-testid="hero-search">
              <div className="mb-4">
                <SmartSearch />
                <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)] mt-2 text-center">AI-powered · or use filters below</div>
              </div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[var(--primary)]">Buy &amp; Sell</div>
                <div className="text-xs text-[var(--ink-muted)]">Refine your search</div>
              </div>
              <FilterBar filters={filters} onChange={setFilters} />
              <div className="mt-3 flex justify-end">
                <button type="submit" className="btn-primary" data-testid="hero-search-btn">
                  Explore Listings <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              <div>
                <div className="font-mono text-3xl font-bold text-[var(--teak)]" data-testid="stat-properties">{stats?.total_properties ?? "—"}</div>
                <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)] mt-1">Live Listings</div>
              </div>
              <div>
                <div className="font-mono text-3xl font-bold text-[var(--teak)]">{stats?.neighborhoods ?? "—"}</div>
                <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)] mt-1">Neighborhoods</div>
              </div>
              <div>
                <div className="font-mono text-3xl font-bold text-[var(--teak)]">{stats?.agents ?? "—"}</div>
                <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)] mt-1">Verified Agents</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative fade-in-up" style={{ animationDelay: "150ms" }}>
            <div className="aspect-[4/5] rounded-[2rem] overflow-hidden border border-[var(--line)] relative">
              <img src={HERO_IMG} alt="Mysuru heritage villa" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -top-4 -right-4 w-28 h-28 rounded-full flex flex-col items-center justify-center text-center text-white" style={{ background: "var(--emerald)" }}>
              <span className="text-[10px] uppercase tracking-widest">Chamundi</span>
              <span className="font-serif text-xl">View</span>
              <span className="text-[10px] uppercase tracking-widest">Verified</span>
            </div>
          </div>
        </div>
      </section>

      {/* Value prop */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-20">
        <div className="divider-ornament mb-10">
          <span className="section-eyebrow">Why NimmaSwathu</span>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: MapPin, title: "Street-level insight", body: "Every listing mapped with Walk, Transit & Chamundi-view scores for Mysuru's unique localities." },
            { icon: Shield, title: "Verified agents only", body: "RERA-registered agents, multilingual (Kannada, English, Hindi, Tamil) and ground-truthed." },
            { icon: Users, title: "Owners welcome, zero commission", body: "Peer-to-peer listings — post directly, chat on WhatsApp, no middleman cuts." },
          ].map((v) => (
            <div key={v.title} className="p-7 rounded-2xl bg-white border border-[var(--line)]">
              <v.icon className="w-6 h-6 text-[var(--primary)]" />
              <h3 className="font-serif text-2xl mt-4 text-[var(--teak)]">{v.title}</h3>
              <p className="text-sm text-[var(--ink-muted)] mt-2 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Neighborhoods section — removed per request */}

      {/* Featured properties section — removed per request */}

      {/* Map teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-24">
        <div className="rounded-3xl overflow-hidden border border-[var(--line)] bg-white p-6 lg:p-10">
          <div className="grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2 flex flex-col justify-center">
              <span className="section-eyebrow">Hyperlocal map</span>
              <h2 className="font-serif text-3xl sm:text-4xl text-[var(--teak)] mt-2">See every listing on the map</h2>
              <p className="text-[var(--ink-muted)] mt-4 leading-relaxed">
                Neighborhood boundaries, price pins, and walkability layers — explore Mysuru block by block.
              </p>
              <Link to="/map" className="btn-primary mt-6 self-start" data-testid="map-cta-btn">
                <MapPin className="w-4 h-4" /> Open full map
              </Link>
            </div>
            <div className="lg:col-span-3 h-[380px]">
              <LeafletMap properties={featured} neighborhoods={neighborhoods} />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-24">
        <div className="rounded-3xl p-10 lg:p-14 text-center" style={{ background: "var(--teak)" }}>
          <HomeIcon className="w-10 h-10 mx-auto text-[var(--gold)]" />
          <h2 className="font-serif text-3xl sm:text-4xl text-white mt-4">Own a property in Mysuru?</h2>
          <p className="text-[var(--gold)] mt-3 max-w-xl mx-auto">
            List it in 3 minutes — no commission, direct inquiries, verified-owner badge.
          </p>
          <button onClick={() => onAuth?.("register")} className="btn-primary mt-6" data-testid="cta-list-property">
            List your property free
          </button>
        </div>
      </section>
    </div>
  );
};
